import React, { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Row } from 'primereact/row'
import { Toolbar } from 'primereact/toolbar'

import MainContentLayout from '../Layout/MainContentLayout'
import server from '../../server'
import {
  DateFilterTemplate,
  DropDownTemplate,
  InputFieldTemplate,
  MinMaxTemplate,
} from '../shared/FilterTemplates'
import { formatCurrency, formatDate, formatPercentage } from '../shared/utils'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

// Import system logos
import originsDirect from '../../assets/images/ORIGINS-DIRECT.png'
import originsManganegment from '../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirectInstant from '../../assets/images/ORIGINS-DIRECT-INSTANT.png'

//theme
import 'primereact/resources/themes/lara-light-indigo/theme.css'
//core
import 'primereact/resources/primereact.min.css'
//icons
import 'primeicons/primeicons.css'

export default function AdminOrders() {
  const dt = useRef(null)
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [orders, setOrders] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [csvLoading, setcsvLoading] = useState(false)
  const [zipLoading, setZipLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [manufacturers, setManufacturers] = useState([])

  const navigate = useNavigate()

  const [lazyState, setLazyState] = useState(() =>
    getInitialPaginationState(user, {
      sortField: '',
      sortOrder: '',
      filters: {
        customerAccountCode: { value: '' },
        customer: { value: '' },
        status: { value: '' },
        'supplier.name': { value: '' },
        supplierOrderNo: { value: '' },
        totalInvoicePa: { value: '' },
        totalInvoiceAmount: { value: '' },
        totalPaPercentage: { value: '' },
        orderDate: { value: '' },
        system: { value: '' },
        'pharmacy.name': { value: '' },
        'pharmacy.user.accountNo': { value: '' },
        manufacturer: { value: '' },
      },
    })
  )

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const initLazyState = () => {
    setLazyState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        customerAccountCode: { value: '' },
        customer: { value: '' },
        status: { value: '' },
        'supplier.name': { value: '' },
        supplierOrderNo: { value: '' },
        totalInvoicePa: { value: '' },
        totalInvoiceAmount: { value: '' },
        totalPaPercentage: { value: '' },
        orderDate: { value: '' },
        system: { value: '' },
        'pharmacy.name': { value: '' },
        'pharmacy.user.accountNo': { value: '' },
        manufacturer: { value: '' },
      },
    }))
  }

  // Fetch manufacturers list - only available ones
  useEffect(() => {
    server
      .get('/manufacturers?ordable=true&comingSoon=false')
      .then((response) => {
        console.log('Manufacturers API response:', response.data)
        // The API returns { Manufacturers: [...], totalCount: ... }
        let manufacturersList = []
        if (response.data && response.data.Manufacturers) {
          manufacturersList = response.data.Manufacturers
        } else if (response.data && response.data.data) {
          manufacturersList = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          manufacturersList = response.data
        }
        
        // Filter for available manufacturers: gpIsOrdable: true, comingSoon: false, and not deleted
        const availableManufacturers = manufacturersList.filter(
          (m) => 
            m && 
            m.id && 
            m.name && 
            m.gpIsOrdable === true && 
            m.comingSoon === false && 
            !m.deletedAt
        )
        
        setManufacturers(availableManufacturers)
        console.log('Available manufacturers set:', availableManufacturers)
      })
      .catch((error) => {
        console.error('Error fetching manufacturers:', error)
      })
  }, [])

  const onRowSelect = (event) => {
    const order = orders.find(
      (item) => item.supplierOrderNo === event.data.supplierOrderNo
    )
    navigate(`${event.data.id}`, { state: order })
  }

  const getData = useCallback(() => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate?.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount?.value
    const supplier = lazyState?.filters?.['supplier.name']?.value
    const paAmount = lazyState?.filters?.totalInvoicePa?.value
    const orderNumber = lazyState?.filters?.supplierOrderNo?.value
    const status = lazyState?.filters?.status?.value
    const system = lazyState?.filters?.system?.value
    const pharmacyName = lazyState?.filters?.['pharmacy.name']?.value
    const accountNo = lazyState?.filters?.['pharmacy.user.accountNo']?.value
    const manufacturer = lazyState?.filters?.manufacturer?.value

    if (dates && Array.isArray(dates) && dates.length > 0 && dates[0]) {
      const currentDate = new Date()
      startDate = formatDate(dates[0])
      endDate = dates[1] ? formatDate(dates[1]) : formatDate(currentDate)
    }

    const requestParams =
      `?page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}` +
      `${
        totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''
      }` +
      `${status ? '&status=' + status : ''}` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${startDate ? '&startDate=' + startDate : ''}` +
      `${endDate ? '&endDate=' + endDate : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${system ? '&system=' + system : ''}` +
      `${pharmacyName ? '&pharmacyName=' + pharmacyName : ''}` +
      `${accountNo ? '&accountNo=' + accountNo : ''}` +
      `${manufacturer ? '&manufacturerId=' + manufacturer : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setTableLoading(true)

    server
      .get(`/invoicesHeaders/orders${requestParams}`)
      .then((response) => {
        const ordersData = response.data.data
        // Debug: Check if manufacturer field exists
        if (ordersData && ordersData.length > 0) {
          console.log('Sample order data:', ordersData[0])
        }
        setOrders(ordersData)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
        // setError(null)
      })
      .catch((error) => {
        setTableLoading(false)
        // setError(error.response.data.message)
      })
  }, [lazyState])

  const exportCSV = () => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate?.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount?.value
    const supplier = lazyState?.filters?.['supplier.name']?.value
    const paAmount = lazyState?.filters?.totalInvoicePa?.value
    const orderNumber = lazyState?.filters?.supplierOrderNo?.value
    const status = lazyState?.filters?.status?.value
    const system = lazyState?.filters?.system?.value
    const pharmacyName = lazyState?.filters?.['pharmacy.name']?.value
    const accountNo = lazyState?.filters?.['pharmacy.user.accountNo']?.value
    const manufacturer = lazyState?.filters?.manufacturer?.value

    if (dates && Array.isArray(dates) && dates.length > 0 && dates[0]) {
      const currentDate = new Date()
      startDate = formatDate(dates[0])
      endDate = dates[1] ? formatDate(dates[1]) : formatDate(currentDate)
    }

    const requestParams =
      '?' +
      `${
        totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''
      }` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${startDate ? '&startDate=' + startDate : ''}` +
      `${endDate ? '&endDate=' + endDate : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${pharmacyName ? '&pharmacyName=' + pharmacyName : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${status ? '&status=' + status : ''}` +
      `${system ? '&system=' + system : ''}` +
      `${accountNo ? '&accountNo=' + accountNo : ''}` +
      `${manufacturer && manufacturer !== '' ? '&manufacturerId=' + manufacturer : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setcsvLoading(true)
    server
      .get(`/invoicesHeaders/exportOrders${requestParams}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'orders.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setcsvLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
        setcsvLoading(false)
      })
  }

  const exportZip = () => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate?.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount?.value
    const supplier = lazyState?.filters?.['supplier.name']?.value
    const paAmount = lazyState?.filters?.totalInvoicePa?.value
    const orderNumber = lazyState?.filters?.supplierOrderNo?.value
    const status = lazyState?.filters?.status?.value
    const system = lazyState?.filters?.system?.value
    const pharmacyName = lazyState?.filters?.['pharmacy.name']?.value
    const accountNo = lazyState?.filters?.['pharmacy.user.accountNo']?.value
    const manufacturer = lazyState?.filters?.manufacturer?.value

    if (dates && Array.isArray(dates) && dates.length > 0 && dates[0]) {
      const currentDate = new Date()
      startDate = formatDate(dates[0])
      endDate = dates[1] ? formatDate(dates[1]) : formatDate(currentDate)
    }

    const requestParams =
      '?' +
      `${
        totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''
      }` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${startDate ? '&startDate=' + startDate : ''}` +
      `${endDate ? '&endDate=' + endDate : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${pharmacyName ? '&pharmacyName=' + pharmacyName : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${status ? '&status=' + status : ''}` +
      `${system ? '&system=' + system : ''}` +
      `${accountNo ? '&accountNo=' + accountNo : ''}` +
      `${
        manufacturer && manufacturer !== ''
          ? '&manufacturerId=' + manufacturer
          : ''
      }` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setZipLoading(true)
    server
      .get(`/invoicesHeaders/exportOrdersZip${requestParams}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'orders_export.zip')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setZipLoading(false)
      })
      .catch(() => {
        setZipLoading(false)
      })
  }
  useEffect(() => {
    checkClearBtn()
    getData()
  }, [lazyState, getData])

  useEffect(() => initLazyState(), [])

  const onPage = (event) => {
    setLazyState((prevState) => {
      const preservedFilters = {}
      Object.keys(prevState.filters).forEach((key) => {
        const filterValue = prevState.filters[key]?.value
        if (Array.isArray(filterValue)) {
          preservedFilters[key] = {
            value: filterValue.length > 0 ? [...filterValue] : filterValue,
          }
        } else {
          preservedFilters[key] = prevState.filters[key]
            ? { ...prevState.filters[key] }
            : { value: '' }
        }
      })

      const next = applyPageEvent(prevState, event)
      return {
        ...next,
        filters: preservedFilters,
        sortField:
          event.sortField !== undefined ? event.sortField : prevState.sortField,
        sortOrder:
          event.sortOrder !== undefined ? event.sortOrder : prevState.sortOrder,
      }
    })
  }

  const onSort = (event) => {
    setLazyState(event)
  }

  const onFilter = (event) => {
    event.first = 0
    event.page = 0
    setLazyState(event)
  }

  // BODY TEMPLATES
  const statusBodyTemplate = (order) => {
    let statusColor
    let textColor
    switch (order.status) {
      case 'Cancelled':
        statusColor = '#EF4444'
        textColor = '#EF4444'
        break
      case 'Pending':
        statusColor = '#86888b'
        textColor = '#86888b'
        break
      case 'Received':
        statusColor = '#FFA500'
        textColor = '#FFA500'
        break
      case 'Confirmed':
        statusColor = '#22c55e'
        textColor = '#22c55e'
        break
      default:
        statusColor = '#86888b'
        textColor = '#86888b'
        break
    }

    return (
      <span
        className={`d-flex justify-content-center py-1 rounded-pill`}
        style={{ border: `1px solid ${statusColor}`, color: `${textColor}` }}
      >
        {order.status || '--'}
      </span>
    )
  }

  // System body template for displaying system logos
  const systemBodyTemplate = (order) => {
    // Check if supplier is K&F/K+F - if so, return blank
    const supplierName = order.supplier?.name || ''
    if (supplierName && (
        supplierName.toLowerCase().includes('k&f') || 
        supplierName.toLowerCase().includes('k+f') ||
        supplierName.toLowerCase().includes('k and f') || 
        supplierName.toLowerCase().includes('kandf'))) {
      return <span></span>
    }
    
    // Default to Origins Direct if no system value is provided
    if (!order.system || order.system === 'Origins Direct') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirect}
            alt='Origins Direct'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else if (order.system === 'Origins Direct Instant') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirectInstant}
            alt='Origins Direct Instant'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else if (order.system === 'Origins Management') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsManganegment}
            alt='Origins Management'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else {
      return <span>{order.system}</span>
    }
  }

  const orderTotalTemplate = (order) => formatCurrency(order.totalInvoiceAmount)
  const paAmountBodyTemplate = (order) => formatCurrency(order.totalInvoicePa)
  const orderDateTemplate = (order) => formatDate(order.orderDate)
  const paRatioTemplate = (order) => formatPercentage(order.totalPaPercentage)
  const manufacturerBodyTemplate = (order) => {
    return order.manufacturer || '-'
  }
  const manufacturerCodeBodyTemplate = (order) => {
    // Keep empty for K+F / multi-manufacturer (backend returns null)
    return order.manufacturerCode || ''
  }

  // FILTER TEMPLATES
  const statusFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select Status'
      options={[
        { name: 'Confirmed', value: 'Confirmed' },
        { name: 'Pending', value: 'Pending' },
        { name: 'Received', value: 'Received' },
        { name: 'Cancelled', value: 'Cancelled' },
      ]}
    />
  )

  const systemFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select System'
      options={[
        { name: 'Origins Direct', value: 'OriginsDirect' },
        { name: 'Origins Management', value: 'OriginsManagement' },
        { name: 'Origins Direct Instant', value: 'OriginsDirectInstant' },
      ]}
    />
  )

  const manufacturerFilterElement = (options) => {
    // Filter out any null/undefined manufacturers and ensure we have valid data
    const validManufacturers = (manufacturers || []).filter(
      (m) => m && m.id && m.name
    )
    
    const manufacturerOptions = [
      { name: 'Several', value: 'several' },
      ...validManufacturers.map((m) => ({
        name: m.name,
        value: String(m.id), // Ensure value is a string
      })),
    ]
    
    return (
      <DropDownTemplate
        state={options.value}
        stateHandler={(e) => {
          console.log('Manufacturer filter event:', e)
          // PrimeReact Dropdown with optionValue returns just the value
          const filterValue = e.value
          console.log('Manufacturer filter value to set:', filterValue)
          options.filterCallback(filterValue, options.index)
        }}
        placeholder='Select Manufacturer'
        options={manufacturerOptions}
      />
    )
  }

  const orderTotalFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
    />
  )

  const paAmountFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
    />
  )

  const orderDateFilterElement = (options) => (
    <DateFilterTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
    />
  )

  const orderNoFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Order no.'
    />
  )

  const accountCodeFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Account code'
    />
  )
  const customerNameFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Customer name'
      icon='pi pi-search'
      type='text'
    />
  )
  const supplierFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Supplier name'
    />
  )

  const checkClearBtn = () => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header='Account Code'
          rowSpan={2}
          sortable
          field='pharmacy?.user?.accountNo'
          filter
          filterField='pharmacy.user.accountNo'
          filterElement={accountCodeFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Customer'
          rowSpan={2}
          sortable
          field='pharmacy.name'
          filter
          filterField='pharmacy.name'
          filterElement={customerNameFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Supplier'
          rowSpan={2}
          sortable
          field='supplier.name'
          filter
          filterElement={supplierFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Order Number'
          rowSpan={2}
          sortable
          field='supplierOrderNo'
          filter
          filterElement={orderNoFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Order Date'
          rowSpan={2}
          sortable
          field='orderDate'
          filter
          filterElement={orderDateFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Manufacturer'
          rowSpan={2}
          field='manufacturer'
          body={manufacturerBodyTemplate}
          filter
          filterField='manufacturer'
          filterElement={manufacturerFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Manufacturer Code'
          rowSpan={2}
          field='manufacturerCode'
          body={manufacturerCodeBodyTemplate}
        />
        <Column
          header='Order Total'
          rowSpan={2}
          sortable
          field='totalInvoiceAmount'
          filter
          filterElement={orderTotalFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Systems'
          rowSpan={2}
          sortable
          field='system'
          filter
          filterElement={systemFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Status'
          rowSpan={2}
          sortable
          field='status'
          filter
          filterElement={statusFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column header='Single Source Molecules' colSpan={3} />
        <Column header='Dual Source Molecules' colSpan={3} />
        <Column header='Multi Source Molecules' colSpan={3} />
        <Column
          header='PA Amount'
          rowSpan={2}
          sortable
          field='totalInvoicePa'
          filter
          filterElement={paAmountFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='PA Percentage'
          rowSpan={2}
          sortable
          field='totalInvoicePaPercentage'
        />
      </Row>
      <Row>
        <Column
          header='Total Amount'
          // sortable
          field='molecules.singleSource.totalInvoiceAmount'
        />
        <Column
          header='PA Amount'
          // sortable
          field='molecules.singleSource.totalRefundedAmount'
        />
        <Column
          header='PA Percentage'
          // sortable
          field='molecules.singleSource.paPercentage'
        />
        <Column
          header='Total Amount'
          // sortable
          field='molecules.dualSource.totalInvoiceAmount'
        />
        <Column
          header='PA Amount'
          // sortable
          field='molecules.dualSource.totalRefundedAmount'
        />
        <Column
          header='PA Percentage'
          // sortable
          field='molecules.dualSource.paPercentage'
        />
        <Column
          header='Total Amount'
          // sortable
          field='molecules.multiSource.totalInvoiceAmount'
        />
        <Column
          header='PA Amount'
          // sortable
          field='molecules.multiSource.totalRefundedAmount'
        />
        <Column
          header='PA Percentage'
          // sortable
          field='molecules.multiSource.paPercentage'
        />
      </Row>
    </ColumnGroup>
  )

  // TOOLBAR
  const leftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={initLazyState}
      disabled={clearBtnDisabled}
    />
  )


  // const rightToolbarTemplate = () => (
  //   <Button
  //     label='Export CS'
  //     icon='pi pi-upload'
  //     className='p-button-help'
  //     onClick={exportCSV}
  //   />
  // )

  return (
    <MainContentLayout
      title='Orders'
      dt={exportCSV}
      handleExport={true}
      loading={csvLoading}
      dtZip={exportZip}
      handleExportZip={true}
      zipLoading={zipLoading}
    >
      <Toolbar className='mb-2' left={leftToolbarTemplate} />
      <DataTable
        ref={dt}
        lazy
        showGridlines
        headerColumnGroup={headerGroup}
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalRecords}
        paginator
        value={orders}
        selectionMode='single'
        selection={selectedOrder}
        onSelectionChange={(e) => setSelectedOrder(e.value)}
        dataKey='supplierOrderNo'
        onRowSelect={onRowSelect}
        tableStyle={{ minWidth: '50rem' }}
        loading={tableLoading}
        filters={lazyState.filters}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        rows={lazyState.rows}
        first={lazyState.first}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        removableSort
      >
        <Column field='pharmacy.user.accountNo' />
        <Column field='pharmacy.name' />
        <Column field='supplier.name' />
        <Column field='supplierOrderNo' />
        <Column field='orderDate' body={orderDateTemplate} />
        <Column field='manufacturer' body={manufacturerBodyTemplate} />
        <Column field='manufacturerCode' body={manufacturerCodeBodyTemplate} />
        <Column field='totalInvoiceAmount' body={orderTotalTemplate} />
        <Column field='system' body={systemBodyTemplate} />
        <Column field='status' body={statusBodyTemplate} />
        {/* <Column field='totalInvoicePa' body={paAmountBodyTemplate} />
        <Column field='totalPaPercentage' body={paRatioTemplate} /> */}
        <Column
          field='molecules.singleSource.totalInvoiceAmount'
          body={(order) =>
            formatCurrency(order.molecules.singleSource.totalInvoiceAmount)
          }
        />
        <Column
          field='molecules.singleSource.totalRefundedAmount'
          body={(order) =>
            formatCurrency(order.molecules.singleSource.totalRefundedAmount)
          }
        />
        <Column
          field='molecules.singleSource.paPercentage'
          body={(order) =>
            formatPercentage(order.molecules.singleSource.paPercentage)
          }
        />
        <Column
          field='molecules.dualSource.totalInvoiceAmount'
          body={(order) =>
            formatCurrency(order.molecules.dualSource.totalInvoiceAmount)
          }
        />
        <Column
          field='molecules.dualSource.totalRefundedAmount'
          body={(order) =>
            formatCurrency(order.molecules.dualSource.totalRefundedAmount)
          }
        />
        <Column
          field='molecules.dualSource.paPercentage'
          body={(order) =>
            formatPercentage(order.molecules.dualSource.paPercentage)
          }
        />
        <Column
          field='molecules.multiSource.totalInvoiceAmount'
          body={(order) =>
            formatCurrency(order.molecules.multiSource.totalInvoiceAmount)
          }
        />
        <Column
          field='molecules.multiSource.totalRefundedAmount'
          body={(order) =>
            formatCurrency(order.molecules.multiSource.totalRefundedAmount)
          }
        />
        <Column
          field='molecules.multiSource.paPercentage'
          body={(order) =>
            formatPercentage(order.molecules.multiSource.paPercentage)
          }
        />

        <Column field='totalInvoicePa' body={paAmountBodyTemplate} />
        <Column
          field='totalInvoicePaPercentage'
          header='PA %'
          body={(rowData) =>
            rowData.totalInvoicePaPercentage != null
              ? `${parseFloat(rowData.totalInvoicePaPercentage).toFixed(2)}%`
              : '-'
          }
          sortable
        />
      </DataTable>
      {/* <Toolbar className='mt-2' right={rightToolbarTemplate} /> */}
    </MainContentLayout>
  )
}
